/**
 * RWBR — THE BOOTH: three people talking, with no model in the room.
 *
 * D-EZ-74. Howard, 21 Aug 2026, asked for *"more conversation"* and, asked how
 * far, said: *"All of it and have it be good enough for no api key but amazing
 * with one (like I have and most people will have)."*
 *
 * ⚠ THE FLOOR HAD NO CONVERSATION IN IT AT ALL. `castCall` — the walk that runs
 * when nobody has a key — hardcoded `exchange: []`, with this comment beside it:
 *
 *     "The cast is not an AI feature, but an EXCHANGE is: three people arguing
 *      is the one thing a template cannot fake, so with the model off a beat is
 *      one voice and says so by being one voice."
 *
 * That was a reasonable thing to believe and it is the same thing we believed
 * about the newspaper quotes at D-EZ-71, right up until we built the bank and
 * it turned out fine. What a template cannot fake is an ARGUMENT — two people
 * disagreeing about something neither of them was told. What a template can do
 * perfectly well is the far more common thing a booth actually does: one person
 * says what happened, and the next one picks up A DIFFERENT TRUE FACT FROM THE
 * SAME MOMENT and says what it means from where they sit.
 *
 * That is what this is. Every answer here is built from a fact the beat is
 * already carrying, so:
 *
 *   • D-003 holds exactly as it does everywhere else — nothing in this file can
 *     say anything the ledger did not say first. The frames are reactions; the
 *     substance is quoted.
 *   • A beat with nothing left to add produces NO exchange rather than a
 *     manufactured one. Silence is still a choice, inside a beat as well as
 *     between beats.
 *   • With a key, `narrateCall` overwrites all of this with people who can
 *     genuinely answer each other. This is the floor, not the ceiling.
 *
 * WHO SAYS WHAT IS NOT DECORATION. Reba Vance answers with the reason, Ned
 * Coyle answers with what is in front of him, Marty Kell answers by handing it
 * back to the race. A bank that gave all three the same shelf would produce
 * three people agreeing, which is worse than one person talking.
 */

import type { BoothBeat } from './booth.ts';
import { COLOUR_KINDS, type Fact, type FactKind } from './researcher.ts';
import type { MemberId } from './cast.ts';

/** Deterministic on the turn, never random — the same race calls the same way. */
const pick = <T>(pool: readonly T[], n: number): T => pool[Math.abs(n) % pool.length];

const cap = (s: string): string => (s.length === 0 ? s : s[0].toUpperCase() + s.slice(1));

/**
 * Drop the fact into the frame and fix the seam.
 *
 * ⚠ THE FACT CLAUSES ARE WRITTEN LOWER-CASE ON PURPOSE — `factClause` in
 * `lines.ts` joins them mid-sentence with commas, so "he is fuming" is the
 * correct stored form. Here they sometimes land at the START of a sentence
 * instead, and the first version produced *"I can hear him from the wall. he is
 * fuming, and everybody around him knows it."*
 *
 * So the seam is repaired rather than the storage changed: a clause that begins
 * the line, or that follows a full stop, gets its first letter raised. Nothing
 * else about it is touched, because everything after the first character is the
 * ledger's own words.
 */
function fill(frame: string, factText: string): string {
  const at = frame.indexOf('{f}');
  const startsSentence = at === 0 || /[.!?—:;]\s+$/.test(frame.slice(0, at));
  const said = startsSentence ? cap(factText) : factText;
  return cap(frame.replace('{f}', said).replace(/\s{2,}/g, ' ').trim());
}

/**
 * A shelf is keyed on WHO is answering and WHAT KIND of fact they picked up.
 * `{f}` is replaced by the fact's own clause, verbatim, so the sentence carries
 * the ledger's words and the frame carries the voice.
 */
type Shelf = Record<string, readonly string[]>;

/**
 * REBA VANCE — the analyst. She answers the question after "what happened",
 * which is "why", and she is briefed to take a position rather than describe.
 */
const ANALYST: Shelf = {
  'injury-cost': ['The consequence reaches beyond this race: {f}.', '{f}. That changes the next entry list.'],
  'from-the-back': ['Keep the starting position in mind: {f}.', '{f}. That is the starting point for this run.'],
  'consecutive-led': ['The sustained run at the front: {f}.', '{f}. That is the stretch to compare.'],
  'laps-down': ['The lap status matters here: {f}.', '{f}. Regaining a lap is a separate challenge.'],
  'head-to-head': ['There is history in the race record: {f}.', '{f}. We have already seen this pairing.'],
};

const PIT: Shelf = {
  'cars-retired': ['The confirmed retirements: {f}.', '{f}. Those cars are out of this race.'],
  injury: ['The injury report: {f}.', '{f}. That is what is confirmed.'],
  'pit-stop': ['The result from pit road: {f}.', '{f}. That is how the stop is recorded.'],
  'pit-penalty': ['The penalty is recorded: {f}.', '{f}. That is the ruling on the stop.'],
  crew: ['An update on the crew: {f}.', '{f}. That is the crew situation.'],
};

const HOST: Shelf = {
  'position-gain': ['{f}. That is the move in the order.', 'The gain on this turn: {f}.'],
  'position-loss': ['The other side of the move: {f}.', '{f}. That is where the turn leaves things.'],
  'lead-changes': ['The lead-change count: {f}.', '{f}. That is the record at the front.'],
};

/** Voice comes from what each person notices; a frame never invents a scene. */
const GENERIC: Record<MemberId, readonly string[]> = {
  analyst: ['Put that alongside the result: {f}.', 'There is another part to it: {f}.', '{f}.'],
  pit: ['The update is this: {f}.', '{f}.', 'Here is what is confirmed: {f}.'],
  host: ['{f}.', 'And {f}.', 'The running account: {f}.'],
};

const SHELF: Record<MemberId, Shelf> = { analyst: ANALYST, pit: PIT, host: HOST };

/**
 * The fact this responder should pick up.
 *
 * ⚠ NOT THE SAME ONE THE LEAD LINE USED, wherever there is a choice. Two people
 * saying the same true thing in different words is the exact failure §8.7 was
 * written to stop, and it is much easier to fall into here than anywhere else:
 * everybody is looking at the same moment.
 *
 * Preference order is by who is answering, which is what makes the three of
 * them sound like different jobs rather than one job with three name badges.
 */
const WANTS: Record<MemberId, readonly FactKind[]> = {
  analyst: ['feud', 'injury-cost', 'consecutive-led', 'from-the-back', 'lead-changes', 'chips', 'still-running', 'head-to-head'],
  pit: ['pit-penalty', 'crew', 'pit-stop', 'injury', 'cars-out', 'low-fuel', 'fuming', 'laps-down'],
  host: ['cars-retired', 'lead-changes', 'position-gain', 'position-loss', 'laps-down', 'still-running'],
};

function factFor(who: MemberId, available: readonly Fact[], used: ReadonlySet<string>): Fact | null {
  /*
    ⚠ IDENTITY IS NOT AN ANSWER. The car number and the shop are true on every
    turn the man is in, so they are always sitting in the free list, and the
    fall-through picked them constantly: three consecutive beats came out with
    *"The 14 car, and that is not nothing."* / *"The 02 car, and that is not
    nothing."* / *"The 41 car, and that is not nothing."*, and Marty answering
    a wreck with *"And he runs for Furniture Quillen Racing."*

    A responder answers with something that HAPPENED or does not answer at all.
    Colour is for the writer that can weave it into a sentence about something
    else — which is the model, and which is why it still travels in `moreFacts`.
  */
  const free = available.filter((f) => !used.has(f.text) && !COLOUR_KINDS.includes(f.kind));
  if (free.length === 0) return null;
  for (const want of WANTS[who]) {
    const hit = free.find((f) => f.kind === want);
    if (hit) return hit;
  }
  return free[0];
}

/**
 * The answering turns for one beat, with no network anywhere.
 *
 * Returns `[]` when there is nothing left that is true and unsaid — which is
 * most beats, and is correct. A booth that convenes a panel every time has no
 * way left to signal that something mattered.
 */
export function bankExchange(
  beat: BoothBeat,
  responders: readonly MemberId[],
): { who: MemberId; line: string }[] {
  if (responders.length === 0) return [];

  /*
    The wider set, minus what the lead line already said. `moreFacts` is a
    superset of `facts` (D-EZ-74) and this is the reason it exists on the
    deterministic path as well as the model one: the second and third voices
    need something the first one did not use.
  */
  const spoken = new Set(beat.facts.map((f) => f.text));
  const available = (beat.moreFacts.length > 0 ? beat.moreFacts : beat.facts).filter(
    (f) => !spoken.has(f.text) || beat.facts.length === 0,
  );
  if (available.length === 0) return [];

  const used = new Set<string>();
  const out: { who: MemberId; line: string }[] = [];

  for (let i = 0; i < responders.length; i++) {
    const who = responders[i];
    const fact = factFor(who, available, used);
    if (!fact) break;
    used.add(fact.text);

    const shelf = SHELF[who][fact.kind] ?? GENERIC[who];
    // The turn AND the seat index, so two responders on the same beat never
    // land on the same slot of the same shelf.
    const frame = pick(shelf, beat.turn * 3 + i * 7);
    const line = fill(frame, fact.text);
    out.push({ who, line });
  }

  return out;
}

/** Every line in the bank, for the test that proves none of them repeat. */
export function everyBankLine(): string[] {
  const all: string[] = [];
  for (const shelf of [ANALYST, PIT, HOST]) {
    for (const lines of Object.values(shelf)) all.push(...lines);
  }
  for (const lines of Object.values(GENERIC)) all.push(...lines);
  return all;
}
